/**
 * The ONE session-JWT decoder.
 *
 * Four independent decoders existed across this repo, each with its own idea of
 * which claim spelling counts and its own silent-null behaviour on garbage. That
 * is the same defect shape the auth state machine exists to kill: N independently
 * evaluated predicates that disagree. This module is the survivor for the SDK.
 *
 * Two properties are load-bearing:
 *
 *   1. **Decoding is never a trust decision.** The signature is NOT verified here
 *      and cannot be — the token is the caller's OWN bearer credential, read only
 *      to learn the identity and expiry the backend will authoritatively re-check.
 *      Nothing in this file may be used to *grant* anything.
 *
 *   2. **A decode failure throws, it never degrades.** `MalformedJwtError` is a
 *      typed, catchable error (`.claude/rules/no-fallbacks.md` — no `safeParse`
 *      returning null, no `?? undefined`). Callers that legitimately hold "maybe
 *      garbage in storage" catch that exact type and decide; they are not handed a
 *      null that reads identically to "no such claim".
 *
 * Critically, a decode result may only ever produce a `ClockExpiry` event
 * (`usable -> degraded`, recoverable). It may NEVER be used to reach
 * `NoCredential` — see `authState.ts`'s derivation rule. An unreadable expiry is
 * a fact about THIS process's ability to parse, not an adjudication of the
 * session by the only authority that can adjudicate it (the backend).
 *
 * Runtime: `atob` + `TextDecoder` are globals in both the browser and modern
 * Node, so this typechecks and runs under the SDK's two runtimes without a
 * Node-only `Buffer` reference.
 */

import { MalformedJwtError } from '../errors.js';

/**
 * The claims the platform actually reads off a session JWT.
 *
 * `raw` is the whole decoded payload so a caller needing an unlisted claim does
 * not fork a fifth decoder to get it.
 */
export interface SessionJwtClaims {
  /** Stytch organization id. Null when the session carries no org claim. */
  org_id: string | null;
  /** LocalAgent account id — injected by activate. Null on a claim-less session. */
  account_id: string | null;
  /** LocalAgent user id — injected by activate. Null on a claim-less session. */
  user_id: string | null;
  email: string | null;
  /** `exp` in epoch SECONDS. Null when absent or non-numeric. */
  exp: number | null;
  /** `iat` in epoch SECONDS. Null when absent or non-numeric. */
  iat: number | null;
  /**
   * Platform-admin display claim injected at activate.
   * Gates UI visibility ONLY — the backend re-checks every
   * authorization server-side; a claim of false never grants, and a claim of
   * true proves nothing.
   */
  is_platform_admin: boolean;
  /** The full decoded payload. */
  raw: Record<string, unknown>;
}

/**
 * Claim spellings this repo has actually observed for the organization id, in
 * precedence order. Stytch B2B nests it under a namespaced claim; LocalAgent's
 * own machine tokens use `org_id`; older/other paths used `organization_id` and
 * the camelCase `orgId`.
 */
const STYTCH_ORG_CLAIM = 'https://stytch.com/organization';

function base64UrlDecode(segment: string): string {
  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function stringClaim(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

function numberClaim(payload: Record<string, unknown>, key: string): number | null {
  const value = payload[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** The organization id under any of the four spellings this repo has produced. */
function orgIdClaim(payload: Record<string, unknown>): string | null {
  const direct =
    stringClaim(payload, 'org_id') ??
    stringClaim(payload, 'organization_id') ??
    stringClaim(payload, 'orgId');
  if (direct !== null) return direct;

  const nested = payload[STYTCH_ORG_CLAIM];
  if (nested !== null && typeof nested === 'object') {
    return stringClaim(nested as Record<string, unknown>, 'organization_id');
  }
  return null;
}

/**
 * Decode a session JWT's payload segment WITHOUT verifying its signature.
 *
 * @throws {MalformedJwtError} when the token is not three dot-separated
 * segments, the payload segment is not valid base64url, or the decoded payload
 * is not a JSON object. Never returns null — an undecodable token is a fact the
 * caller must confront, not paper over.
 */
export function decodeSessionJwt(token: string): SessionJwtClaims {
  const segments = token.split('.');
  if (segments.length !== 3) {
    throw new MalformedJwtError(
      `expected 3 dot-separated segments, got ${segments.length}`,
    );
  }
  const payloadSegment = segments[1];
  if (payloadSegment === undefined || payloadSegment === '') {
    throw new MalformedJwtError('payload segment is empty');
  }

  let json: string;
  try {
    json = base64UrlDecode(payloadSegment);
  } catch (cause) {
    throw new MalformedJwtError(
      `payload segment is not valid base64url: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (cause) {
    throw new MalformedJwtError(
      `payload segment is not valid JSON: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new MalformedJwtError('payload is not a JSON object');
  }
  const payload = parsed as Record<string, unknown>;

  return {
    org_id: orgIdClaim(payload),
    account_id: stringClaim(payload, 'account_id'),
    user_id: stringClaim(payload, 'user_id'),
    email: stringClaim(payload, 'email'),
    exp: numberClaim(payload, 'exp'),
    iat: numberClaim(payload, 'iat'),
    is_platform_admin:
      payload['is_platform_admin'] === true || payload['isPlatformAdmin'] === true,
    raw: payload,
  };
}

/**
 * Read a single STRING claim off a session JWT.
 *
 * Returns `undefined` when the claim is absent or non-string — a genuinely
 * different fact from "the token is garbage", which throws. Callers that require
 * the claim convert the `undefined` into their own loud throw (see
 * `HttpLayer.actingAccountId`).
 *
 * @throws {MalformedJwtError} when the token itself cannot be decoded.
 */
export function jwtStringClaim(token: string, claim: string): string | undefined {
  const value = decodeSessionJwt(token).raw[claim];
  return typeof value === 'string' ? value : undefined;
}

/**
 * The `credential_fingerprint` carried on the daemon's `auth:changed` frame:
 * `"<iat>|<exp>"`.
 *
 * It exists so a consumer can tell "the daemon re-emitted its state" from "the
 * daemon minted genuinely different material", WITHOUT ever putting a JWT on the
 * `/events` socket — that socket is unauthenticated loopback (la ADR-010), so no
 * frame may carry a bearer credential.
 *
 * @throws {MalformedJwtError} when the token cannot be decoded, or carries no
 * numeric `iat`/`exp`. A session JWT always has both; a missing one is a broken
 * credential, not a value to substitute a default for.
 */
export function credentialFingerprint(token: string): string {
  const { iat, exp } = decodeSessionJwt(token);
  if (iat === null || exp === null) {
    throw new MalformedJwtError(
      `session JWT must carry numeric iat and exp (got iat=${String(iat)}, exp=${String(exp)})`,
    );
  }
  return `${iat}|${exp}`;
}
